<template>
  <div class="row justify-content-center">
    <div class="col-md-12">
      <h1 class="fs-2 mb-0">Scheduling Report</h1>
      <span class="text-muted">Core and Short Visits in Current Window {{ currentWindow?.name }}</span>
    </div>

    <div class="col-md-12 mt-3">
      <ul class="nav nav-tabs fw-bold gap-1" role="tablist" aria-label="Report Sections">
        <li
          v-for="tab in tabs"
          :key="tab.id"
          class="nav-item"
          role="none"
        >
          <button
            :id="`${tab.id}-tab`"
            type="button"
            class="nav-link px-3 d-flex align-items-center gap-2"
            :class="{ 'active': activeTabId === tab.id }"
            role="tab"
            :aria-controls="`${tab.id}-panel`"
            :aria-selected="activeTabId === tab.id ? 'true' : 'false'"
            :tabindex="activeTabId === tab.id ? '0' : '-1'"
            @click="activeTabId = tab.id"
          >
            <span>{{ tab.name }}</span>
            <span 
              class="badge rounded-pill"
              :class="activeTabId === tab.id ? 'bg-primary' : 'bg-light text-dark'"
            >
              {{ tab.data.value.length }}
            </span>
          </button>
        </li>
      </ul>

      <div class="tab-content bg-white border border-top-0 rounded-bottom p-3">
        <div
          v-if="activeTab" 
          :id="`${activeTab.id}-panel`"
          class="tab-pane active" 
          role="tabpanel"
          tabindex="0"
          :aria-labelledby="`${activeTab.id}-tab`"
        >
          <component 
            :is="activeTab.component" 
            :participants="activeTab.data.value"
            :visit-type="activeTab.visitType"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import ScheduledContent from './tabs/ScheduledContent.vue';
import NotScheduledContent from './tabs/NotScheduledContent.vue';
import CompletedContent from './tabs/CompletedContent.vue';

const props = defineProps({
  scheduledParticipants: { type: Array, default: () => [] },
  notScheduledParticipants: { type: Array, default: () => [] },
  completedParticipants: { type: Array, default: () => [] },
  currentWindow: { type: Object, default: () => ({}) },
});

const searchQuery = ref('');

const separateParticipantsByTaskType = (participants = []) => {
  if (!Array.isArray(participants)) return { core: [], short: [] };

  return participants.reduce(
    (acc, participant) => {
      const tasks = participant?.tasks ?? [];

      const coreTasks = tasks.filter(
        (t) => t?.task_type_status?.task_type?.action === 'core_appointment'
      );
      const shortTasks = tasks.filter(
        (t) => t?.task_type_status?.task_type?.action !== 'core_appointment'
      );

      if (coreTasks.length > 0) {
        acc.core.push({
          ...participant,
          targetMonth: participant.core_target_month,
          tasks: coreTasks,
        });
      }

      if (shortTasks.length > 0) {
        acc.short.push({
          ...participant,
          targetMonth: participant.short_target_month,
          tasks: shortTasks,
        });
      }

      return acc;
    },
    { core: [], short: [] }
  );
};

// Generic Search Filter Helper
const filterBySearch = (list) => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return list;

  return list.filter((p) => {
    const name = `${p.first_name ?? ''} ${p.last_name ?? ''}`.toLowerCase();
    const starId = String(p.star_id ?? '').toLowerCase();
    return name.includes(query) || starId.includes(query);
  });
};

const completedCore = computed(() => separateParticipantsByTaskType(props.completedParticipants).core);
const completedShort = computed(() => separateParticipantsByTaskType(props.completedParticipants).short);

const scheduledCore = computed(() => separateParticipantsByTaskType(props.scheduledParticipants).core);
const scheduledShort = computed(() => separateParticipantsByTaskType(props.scheduledParticipants).short);

const notScheduledCore = computed(() => separateParticipantsByTaskType(props.notScheduledParticipants).core);
const notScheduledShort = computed(() => separateParticipantsByTaskType(props.notScheduledParticipants).short);

const tabs = computed(() => [
  {
    id: 'core-not-scheduled',
    name: 'Core, Not Scheduled',
    component: NotScheduledContent,
    data: notScheduledCore,
    visitType: 'Core',
  },
  {
    id: 'core-scheduled',
    name: 'Core, Scheduled',
    component: ScheduledContent,
    data: scheduledCore,
    visitType: 'Core',
  },
  {
    id: 'core-completed',
    name: 'Core, Completed',
    component: CompletedContent,
    data: completedCore,
    visitType: 'Core',
  },
  {
    id: 'short-not-scheduled',
    name: 'Short, Not Scheduled',
    component: NotScheduledContent,
    data: notScheduledShort,
    visitType: 'Short',
  },
  {
    id: 'short-scheduled',
    name: 'Short, Scheduled',
    component: ScheduledContent,
    data: scheduledShort,
    visitType: 'Short',
  },
  {
    id: 'short-completed',
    name: 'Short, Completed',
    component: CompletedContent,
    data: completedShort,
    visitType: 'Short',
  },
]);

const activeTabId = ref(null);
const activeTab = computed(() => tabs.value.find((t) => t.id === activeTabId.value));

onMounted(() => {
  if (tabs.value.length > 0) {
    activeTabId.value = tabs.value[0].id;
  }
});
</script>